'use client';

import { useEffect, useRef } from 'react';
import { useConvex } from 'convex/react';
import { useUser } from '@clerk/nextjs';
import { initTrack, track } from '@/lib/analytics/track';
import { captureFirstTouch, captureReferralCode } from '@/lib/analytics/attribution';

/**
 * Wires the product-analytics client to Convex + the current user, and fires an
 * `app_open` event (with first-touch UTM/referrer) so DAU/WAU/MAU and the funnel
 * capture browse-only sessions too. Mounted once inside the Convex provider.
 */
export function AnalyticsInit() {
  const convex = useConvex();
  const { user, isLoaded } = useUser();
  const fired = useRef(false);

  useEffect(() => {
    initTrack(convex, () => user?.id);
    captureFirstTouch();
    captureReferralCode();
    if (!isLoaded || fired.current) return;
    fired.current = true;

    const utm: Record<string, string> = {};
    try {
      const p = new URLSearchParams(location.search);
      for (const k of ['utm_source', 'utm_medium', 'utm_campaign']) {
        const val = p.get(k);
        if (val) utm[k] = val;
      }
      if (document.referrer) utm.referrer = new URL(document.referrer).host;
    } catch {
      /* ignore */
    }
    track('app_open', Object.keys(utm).length ? utm : undefined);
  }, [convex, user?.id, isLoaded]);

  return null;
}
